import type { UseQueryResult } from '@tanstack/react-query';
import { ArrowRight, ChevronDown, Layers } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FocusAppsResponse } from './api';
import { EVIDENCE, identityOf, osLabel } from './apps';
import type { FocusConfig } from './config';
import { SCREENS } from '@/app/screens';
import { AppLogo } from '@/components/brand/AppLogo';
import { Checkbox } from '@/components/ui/checkbox';
import { InlineError } from '@/components/ui/inline-error';
import { inputClass } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverHint, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { EvidencePill } from '@/components/ui/tag';
import { getErrorMessage } from '@/api/errors';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/format';

type AppSwitcherProps = {
  /** Selected apps (`appId::OS`); empty with `portfolio` for All apps. */
  selected: string[];
  portfolio: boolean;
  /** Offer "All apps" (the portfolio view). */
  allApps: boolean;
  copy: FocusConfig['appSwitcher'];
  apps: UseQueryResult<FocusAppsResponse>;
  onChange: (apps: string[] | 'all') => void;
};

/**
 * The Focus button and switcher. Clicking an app looks at it alone; ticking
 * several combines them; "All apps" is the explicit portfolio view, offered
 * where the screen's config allows it (Performance). Each app
 * says whether it has enough data for statuses and insights (evidence goes
 * where a conclusion is drawn — here, which app to trust).
 */
export function AppSwitcher({ selected, portfolio, allApps, copy, apps, onChange }: AppSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const list = apps.data?.apps ?? [];
  const first = selected[0] ? identityOf(selected[0], list) : null;
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? list.filter((a) => `${a.name} ${osLabel(a.os)} ${a.app_id}`.toLowerCase().includes(needle))
    : list;

  const pick = (id: string) => {
    setOpen(false);
    onChange([id]);
  };
  const tick = (id: string, on: boolean) => {
    const next = on ? [...new Set([...selected, id])] : selected.filter((x) => x !== id);
    // The last app stays: "no app" is not a scope (All apps is its own choice).
    if (next.length) onChange(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          data-tour="focus-app"
          className="flex min-h-[34px] max-w-full items-center gap-2.5 rounded-ctl border border-line bg-surface py-[3px] pr-2 pl-1 text-left transition-[background-color,border-color] duration-150 hover:border-line-strong hover:bg-wash aria-expanded:border-line-strong aria-expanded:bg-wash"
        >
          {portfolio || !first ? (
            <span aria-hidden="true" className="grid size-[26px] shrink-0 place-items-center rounded-[6px] bg-wash text-muted">
              <Layers className="size-4" />
            </span>
          ) : (
            <AppLogo name={first.name} iconUrl={first.iconUrl} seed={first.id} size={26} />
          )}
          <span className="min-w-0 truncate">
            <span className="sr-only">App: </span>
            <span className="font-semibold">{portfolio || !first ? 'All apps' : first.name}</span>{' '}
            <span className="text-xs text-muted">
              {portfolio || !first ? 'Portfolio' : first.os}
              {!portfolio && selected.length > 1 ? ` + ${selected.length - 1} more` : ''}
            </span>
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent aria-label="Switch app">
        <input
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
          placeholder="Search apps…"
          aria-label="Search apps"
          className={inputClass}
        />
        <PopoverHint>
          Your apps · by spend, last {apps.data?.ranking_window_days ?? 14} days · data needed for {copy.evidenceFor}
        </PopoverHint>
        {apps.isPending ? (
          <div aria-busy="true" className="flex flex-col gap-1 p-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2.5 py-1.5">
                <Skeleton width={16} height={16} />
                <Skeleton width={28} height={28} />
                <span className="flex flex-1 flex-col gap-1.5">
                  <Skeleton width="45%" height={12} />
                  <Skeleton width="30%" height={12} />
                </span>
                <Skeleton width={56} height={12} />
              </div>
            ))}
          </div>
        ) : apps.isError && !apps.data ? (
          <InlineError message={getErrorMessage(apps.error)} onRetry={() => { void apps.refetch(); }} retrying={apps.isFetching} className="my-1" />
        ) : shown.length ? (
          <ul className="m-0 flex list-none flex-col p-0">
            {shown.map((app) => {
              const id = identityOf(app.id, list);
              const evidence = EVIDENCE[app.evidence];
              const current = !portfolio && selected.length === 1 && selected[0] === app.id;
              return (
                <li key={app.id} className="flex items-center gap-1">
                  <span className="ml-1.5 flex">
                    <Checkbox
                      checked={!portfolio && selected.includes(app.id)}
                      onCheckedChange={(on) => { tick(app.id, on); }}
                      aria-label={`Include ${id.name} ${id.os}`}
                    />
                  </span>
                  <button
                    type="button"
                    aria-current={current || undefined}
                    onClick={() => { pick(app.id); }}
                    className={cn(
                      'grid min-w-0 flex-1 grid-cols-[28px_1fr_auto] items-center gap-2.5 rounded-ctl border-0 bg-transparent px-1.5 py-2 text-left hover:bg-wash',
                      current && 'bg-wash',
                    )}
                  >
                    <AppLogo name={id.name} iconUrl={id.iconUrl} seed={id.id} size={28} />
                    <span className="flex min-w-0 flex-col items-start gap-0.5">
                      <span className="max-w-full truncate">
                        <b className="font-semibold">{id.name}</b> <span className="text-muted">{id.os}</span>
                      </span>
                      <EvidencePill level={evidence.level}>{evidence.label}</EvidencePill>
                    </span>
                    <span className="num text-xs">{formatMoney(app.spend)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="m-0 px-1.5 py-4 text-center text-sm text-muted">
            {needle ? `No apps match “${query.trim()}”.` : 'No apps with data yet.'}
          </p>
        )}
        <p className="m-1.5 text-xs text-muted">
          {copy.hint}
          {!allApps && copy.portfolioOnPerformance ? (
            <>
              {' '}
              All apps is available on{' '}
              <Link to={SCREENS.performance.path} search={{ all_apps: '1' }} className="font-medium text-accent-ink hover:underline">
                Performance
              </Link>
              .
            </>
          ) : null}
        </p>
        {allApps ? (
          <button
            type="button"
            aria-current={portfolio || undefined}
            onClick={() => {
              setOpen(false);
              onChange('all');
            }}
            className="flex w-full items-center gap-1 rounded-ctl border-0 bg-transparent px-1.5 py-2 text-left text-xs font-medium text-muted hover:bg-wash hover:text-fg aria-[current]:text-fg"
          >
            All apps (portfolio view)
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
